import { Link, useLocation, useNavigate } from 'react-router-dom'
import { flushSync } from 'react-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowRight, Briefcase, Building2, Layers3, Loader2, RefreshCw, Star } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromPost, getServiceInstanceIdFromSearch, withServiceInstanceId, buildServiceVariantHomePath } from '@/lib/service-instance'
import { evaluateRouteAccess } from '@/lib/route-access'
import { authApi } from '@/services/auth'
import { workersApi } from '@/services/workers'
import { getBookmarkedPostKey, type Post, useAuthStore } from '@/stores/auth-store'

type ServiceInstanceStatusValue = string | number | null | undefined
type RestrictionScope = 'organisation' | 'role'

const APPROVED_STATUS_LABEL = 'Approved'

const getServiceRegistrationStatusLabel = (status: ServiceInstanceStatusValue): string | null => {
  if (status === null || status === undefined || status === '') {
    return null
  }

  const normalized = String(status).trim().toLowerCase()

  if (normalized === '1' || normalized === 'approved' || normalized === 'active') {
    return APPROVED_STATUS_LABEL
  }

  if (normalized === '0' || normalized === 'pending') {
    return 'Pending'
  }

  if (normalized === '2' || normalized === 'rejected') {
    return 'Rejected'
  }

  if (normalized === '3' || normalized === 'suspended') {
    return 'Suspended'
  }

  if (normalized === '4' || normalized === 'terminated') {
    return 'Terminated'
  }

  if (!normalized) {
    return null
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

const getStatusBadgeClassName = (statusLabel: string): string => {
  const normalized = statusLabel.trim().toLowerCase()

  if (normalized === 'pending' || normalized === 'suspended') {
    return 'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-900/70 dark:bg-amber-900/40 dark:text-amber-100'
  }

  if (normalized === 'rejected' || normalized === 'terminated') {
    return 'border-rose-200 bg-rose-100 text-rose-900 dark:border-rose-900/70 dark:bg-rose-900/40 dark:text-rose-100'
  }

  return 'border-border/70 bg-muted/40 text-muted-foreground'
}

const isApprovedServiceStatusLabel = (statusLabel: string | null): boolean => {
  return !statusLabel || statusLabel === APPROVED_STATUS_LABEL
}

const getRestrictedSelectionLabel = (statusLabel: string | null, isSelected: boolean): string => {
  switch (statusLabel) {
    case 'Pending':
      return isSelected ? 'Current Role Awaiting Approval' : 'Awaiting Approval'
    case 'Rejected':
      return isSelected ? 'Current Role Rejected' : 'Rejected'
    case 'Suspended':
      return isSelected ? 'Current Role Suspended' : 'Suspended'
    case 'Terminated':
      return isSelected ? 'Current Role Terminated' : 'Terminated'
    default:
      return isSelected ? 'Unavailable' : 'Role Unavailable'
  }
}

const getRestrictedSelectionMessage = (
  statusLabel: string | null,
  isSelected: boolean,
  scope: RestrictionScope,
): string => {
  switch (statusLabel) {
    case 'Pending':
      if (scope === 'organisation') {
        return isSelected
          ? 'Organisation approval is pending. You can keep using your profile while this role waits for approval.'
          : 'Organisation approval is pending. You can switch after approval is completed.'
      }

      return isSelected
        ? 'Role is pending for approval. You can keep using your profile while approval is in progress.'
        : 'Role is pending for approval. You can switch after approval is completed.'
    case 'Rejected':
      return scope === 'organisation'
        ? 'This role is unavailable because the organisation access request was rejected.'
        : 'This role is unavailable because the role access request was rejected.'
    case 'Suspended':
      return scope === 'organisation'
        ? 'This role is unavailable because the organisation is currently suspended.'
        : 'This role is unavailable because the role is currently suspended.'
    case 'Terminated':
      return scope === 'organisation'
        ? 'This role is unavailable because the organisation access has been terminated.'
        : 'This role is unavailable because the role access has been terminated.'
    default:
      return scope === 'organisation'
        ? `This role is unavailable while the organisation status is ${statusLabel?.toLowerCase()}.`
        : `This role is unavailable while the role status is ${statusLabel?.toLowerCase()}.`
  }
}

const getOrganisationStatusSummary = (statusLabel: string | null): string | null => {
  switch (statusLabel) {
    case 'Pending':
      return 'Organisation approval is pending for this business. Role switching will stay limited until approval is completed.'
    case 'Rejected':
      return 'Organisation access was rejected for this business. This role will remain unavailable until access is granted again.'
    case 'Suspended':
      return 'Organisation access is currently suspended for this business. Role switching is unavailable while the suspension remains in place.'
    case 'Terminated':
      return 'Organisation access was terminated for this business. This role cannot be used unless access is restored.'
    default:
      return statusLabel
        ? `Organisation access is currently ${statusLabel.toLowerCase()} for this business.`
        : null
  }
}

const isPendingRegistrationStatus = (value: unknown): boolean => {
  const normalized = String(value ?? '').trim().toLowerCase()
  return normalized === '1' || normalized === 'pending'
}

const dedupeRoleSwitcherPosts = (posts: Post[]): Post[] => {
  const byKey = new Map<string, Post>()
  const readCreatedOn = (entry: Post): string => {
    const record = entry as unknown as Record<string, unknown>
    return String(record.created_on || '')
  }

  for (const post of posts) {
    const key = getBookmarkedPostKey(post) || String(post.id)
    if (!key) {
      continue
    }

    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, post)
      continue
    }

    const existingTime = Date.parse(readCreatedOn(existing))
    const nextTime = Date.parse(readCreatedOn(post))
    if (Number.isFinite(nextTime) && (!Number.isFinite(existingTime) || nextTime > existingTime)) {
      byKey.set(key, post)
    }
  }

  return Array.from(byKey.values())
}

const filterRoleSwitcherPostsForUser = (
  posts: Post[],
  user: ReturnType<typeof useAuthStore.getState>['user'],
): Post[] => {
  if (!user || posts.length === 0) {
    return posts
  }

  const normalize = (value: unknown) => String(value ?? '').trim().toLowerCase()
  const userTokens = new Set(
    [
      user.first_name,
      user.last_name,
      [user.first_name, user.last_name].filter(Boolean).join(' '),
      user.username,
      user.email,
    ]
      .map((value) => normalize(value))
      .filter(Boolean),
  )

  const mine = posts.filter((post) => {
    const record = post as unknown as Record<string, unknown>
    const userData = record.user_data && typeof record.user_data === 'object'
      ? (record.user_data as Record<string, unknown>)
      : null
    const postTokens = [
      record.user_name,
      userData?.name,
      userData?.email,
      record.user_email,
    ]
      .map((value) => normalize(value))
      .filter(Boolean)

    // If row has no user identity fields, keep it so we don't hide valid roles.
    if (postTokens.length === 0) {
      return true
    }

    return postTokens.some((token) => userTokens.has(token))
  })

  return mine.length > 0 ? mine : posts
}

export default function PostsSelectionPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const hasHydrated = useAuthStore((state) => state.hasHydrated)
  const user = useAuthStore((state) => state.user)
  const currentPost = useAuthStore((state) => state.currentPost)
  const availablePosts = useAuthStore((state) => state.availablePosts)
  const bookmarkedPostKeys = useAuthStore((state) => state.bookmarkedPostKeys)
  const recentPostKeys = useAuthStore((state) => state.recentPostKeys)
  const setPost = useAuthStore((state) => state.setPost)
  const setActiveWorker = useAuthStore((state) => state.setActiveWorker)
  const setServiceInstanceId = useAuthStore((state) => state.setServiceInstanceId)
  const toggleBookmarkedPost = useAuthStore((state) => state.toggleBookmarkedPost)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)

  // Only use serviceInstanceId for navigation, not for refresh
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search) || storedServiceInstanceId
  const profileServiceInstanceId = getServiceInstanceIdFromPost(currentPost) || serviceInstanceId
  const isRolesSection = location.pathname === '/org/roles' || location.pathname === '/roles'
  const isPendingUser = isPendingRegistrationStatus(user?.registration_status)

  // --- Data fetching: block the chooser until the initial role list resolves ---
  const [resolvedPosts, setResolvedPosts] = useState<Post[]>([])
  const [lastFetchedRoleCount, setLastFetchedRoleCount] = useState<number | null>(null)
  const [lastFetchedRawRoleCount, setLastFetchedRawRoleCount] = useState<number | null>(null)
  const [isInitialLoadPending, setIsInitialLoadPending] = useState(true)
  const [initialLoadError, setInitialLoadError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [isSelectingRole, setIsSelectingRole] = useState(false)
  const [selectingRoleLabel, setSelectingRoleLabel] = useState('')

  const fetchPosts = useCallback(async (showToast = false) => {
    // Remove service_instance_id from the URL on refresh
    const params = new URLSearchParams(location.search)
    if (params.has('service_instance_id')) {
      params.delete('service_instance_id')
      navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true })
    }
    setIsFetching(true)
    try {
      const result = await authApi.listPostsDirectWithMessage()
      const mine = filterRoleSwitcherPostsForUser(result.posts, user)
      const deduped = dedupeRoleSwitcherPosts(mine)
      setResolvedPosts(deduped)
      setLastFetchedRoleCount(deduped.length)
      const debugRawCount = Number((window as any).__lastUserPostListDebug?.rawCount)
      setLastFetchedRawRoleCount(Number.isFinite(debugRawCount) ? debugRawCount : null)
      if (showToast) {
        toast.success(result.message || 'Roles refreshed')
      }
    } catch (err) {
      if (showToast) {
        toast.error(getErrorMessage(err))
      }
    } finally {
      setIsFetching(false)
    }
  }, [location, navigate, user])

  useEffect(() => {
    if (!hasHydrated) return
    let isActive = true

    if (availablePosts.length > 0) {
      const mine = filterRoleSwitcherPostsForUser(availablePosts, user)
      const deduped = dedupeRoleSwitcherPosts(mine)
      setResolvedPosts(deduped)
      setLastFetchedRoleCount(deduped.length)
    }

    setIsInitialLoadPending(true)
    setInitialLoadError(null)
    setIsFetching(true)

    // Always refresh from UserPostList so newly assigned roles appear without waiting for cache expiry.
    void authApi.listPostsDirectWithMessage()
      .then((result) => {
        if (isActive) {
          const mine = filterRoleSwitcherPostsForUser(result.posts, user)
          const deduped = dedupeRoleSwitcherPosts(mine)
          setResolvedPosts(deduped)
          setLastFetchedRoleCount(deduped.length)
          const debugRawCount = Number((window as any).__lastUserPostListDebug?.rawCount)
          setLastFetchedRawRoleCount(Number.isFinite(debugRawCount) ? debugRawCount : null)
        }
      })
      .catch((err) => {
        if (isActive) {
          setInitialLoadError(getErrorMessage(err))
        }
      })
      .finally(() => {
        if (isActive) {
          setIsFetching(false)
          setIsInitialLoadPending(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [hasHydrated, user])

  const sortedPosts = useMemo(() => {
    const visiblePosts = resolvedPosts.length > 0 ? resolvedPosts : availablePosts

    if (bookmarkedPostKeys.length === 0 && recentPostKeys.length === 0) {
      return visiblePosts
    }

    return [...visiblePosts].sort((left, right) => {
      const leftKey = getBookmarkedPostKey(left)
      const rightKey = getBookmarkedPostKey(right)
      const leftBookmarked = bookmarkedPostKeys.includes(leftKey)
      const rightBookmarked = bookmarkedPostKeys.includes(rightKey)

      if (leftBookmarked !== rightBookmarked) {
        return leftBookmarked ? -1 : 1
      }

      const leftRecentIndex = recentPostKeys.indexOf(leftKey)
      const rightRecentIndex = recentPostKeys.indexOf(rightKey)
      const leftRecent = leftRecentIndex >= 0
      const rightRecent = rightRecentIndex >= 0

      if (leftRecent !== rightRecent) {
        return leftRecent ? -1 : 1
      }

      if (leftRecent && rightRecent) {
        return leftRecentIndex - rightRecentIndex
      }

      return 0
    })
  }, [availablePosts, bookmarkedPostKeys, recentPostKeys, resolvedPosts])

  const hasVisiblePosts = sortedPosts.length > 0

  const handleSelectPost = async (postKey: string) => {
    if (isSelectingRole) {
      return
    }

    const post = sortedPosts.find((entry) => getBookmarkedPostKey(entry) === postKey)
    if (!post) {
      return
    }

    const selectedServiceInstanceId = getServiceInstanceIdFromPost(post) || serviceInstanceId
    flushSync(() => {
      setIsSelectingRole(true)
      setSelectingRoleLabel(post.role_name || 'selected role')
      setPost(post)
      setServiceInstanceId(selectedServiceInstanceId || null)
    })

    try {
      await authApi.hydrateSelectedRoleCapabilities(post)
    } catch {
      // Continue with selected role context if hydration fails.
    }

    const hydratedPost = useAuthStore.getState().currentPost
    const activePost = hydratedPost ?? post
    const activeServiceInstanceId = getServiceInstanceIdFromPost(activePost) || selectedServiceInstanceId
    const preferredRedirectTarget = buildServiceVariantHomePath(activePost, activeServiceInstanceId)
    const preferredPathname = preferredRedirectTarget.split('?')[0] || preferredRedirectTarget
    const redirectTarget = evaluateRouteAccess(preferredPathname, activePost).authorized
      ? preferredRedirectTarget
      : withServiceInstanceId('/dashboard', activeServiceInstanceId)
    navigate(redirectTarget, { replace: true })

    void (async () => {

      try {
        const profiles = await workersApi.getMyWorkerProfiles({
          postId: activePost.id,
          includePending: true,
          serviceInstanceId: activeServiceInstanceId,
        })
        const approvedProfile = profiles.find((profile) => profile.is_selectable)
        setActiveWorker(approvedProfile?.id ?? null)
      } catch {
        // Keep current worker selection if profile fetch fails.
      }
    })()
  }

  return (
    <div className="space-y-6">
      {isSelectingRole ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-sm border-primary/30 shadow-2xl">
            <CardHeader className="items-center text-center">
              <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Loader2 className="h-7 w-7 animate-spin" />
              </div>
              <CardTitle>Switching role</CardTitle>
              <CardDescription>
                Loading permissions and workspace for {selectingRoleLabel}. Please wait...
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      ) : null}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-3xl">
          <div className="mt-2 flex items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              {isRolesSection ? 'Roles' : 'Choose your access'}
            </h1>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => void fetchPosts(true)}
              disabled={isFetching || isSelectingRole}
              aria-label="Refresh roles"
              title="Refresh roles"
            >
              <RefreshCw className={isFetching ? 'animate-spin' : ''} />
            </Button>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            {isRolesSection
              ? 'View the roles assigned to your account and switch the active post for this workspace.'
              : 'Select one option to continue.'}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">Star roles to pin them to the top. Recently used roles appear next.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 self-start">
          {isRolesSection && !isPendingUser ? (
            <Button asChild variant="outline">
              <Link to={withServiceInstanceId('/profile', profileServiceInstanceId || null)}>
                View Or Edit Profile
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      {hasHydrated && isInitialLoadPending ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Card key={index} className="overflow-hidden">
              <div className="h-0.5 w-full bg-gradient-to-r from-primary/40 via-primary/15 to-transparent" />
              <CardHeader className="p-4 pb-2">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-14 w-14 rounded-xl" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 px-4 pb-4 pt-2">
                <Skeleton className="h-12 w-full rounded-lg" />
                <Skeleton className="h-12 w-full rounded-lg" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      {!hasHydrated ? (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle>Loading roles...</CardTitle>
            <CardDescription>Preparing your account context.</CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {!isInitialLoadPending && initialLoadError && !hasVisiblePosts ? (
        <Card className="border-rose-200 bg-rose-50/70 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl text-rose-950">Unable to load roles</CardTitle>
            <CardDescription className="text-rose-900/80">{initialLoadError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button type="button" variant="outline" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {isPendingUser ? (
        <Card className="border-amber-200 bg-amber-50/70 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl text-amber-950">Your account is still pending</CardTitle>
            <CardDescription className="text-amber-900/80">
              You can review the roles assigned to your account, but role switching remains disabled until your account is approved. Profile access is unavailable until approval is completed.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {!isInitialLoadPending && !initialLoadError && !hasVisiblePosts ? (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle>
              {isRolesSection ? 'No roles assigned' : 'No posts available'}
            </CardTitle>
            <CardDescription>
              {isRolesSection
                ? 'Your account is signed in, but there are no assigned roles available for switching yet.'
                : 'Your account is signed in, but there are no posts assigned to it yet.'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : !isInitialLoadPending && hasVisiblePosts ? (
        <>
          <Card className="border-dashed">
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Role Count</CardTitle>
              <CardDescription>
                Showing {lastFetchedRoleCount ?? sortedPosts.length} role(s)
                {lastFetchedRawRoleCount !== null ? `, raw API rows: ${lastFetchedRawRoleCount}` : ''}.
              </CardDescription>
            </CardHeader>
          </Card>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {sortedPosts.map((post) => {
            const postKey = getBookmarkedPostKey(post)
            const isSelected = currentPost ? getBookmarkedPostKey(currentPost) === postKey : false
            const isBookmarked = bookmarkedPostKeys.includes(postKey)
            const isRecent = recentPostKeys.includes(postKey)
            const postReference = String(post.service_instance_id || post.service_instance || '').trim()

            // Status comes directly from post fields — the API returns them on every fetch
            const serviceBranchStatusLabel = getServiceRegistrationStatusLabel(post.service_instance_status ?? null)
            const organisationStatusLabel = getServiceRegistrationStatusLabel(post.org_status ?? null)

            const restrictionScope = !isApprovedServiceStatusLabel(serviceBranchStatusLabel)
              ? 'role'
              : !isApprovedServiceStatusLabel(organisationStatusLabel)
                ? 'organisation'
                : null
            const effectiveRestrictionStatusLabel = restrictionScope === 'role'
              ? serviceBranchStatusLabel
              : restrictionScope === 'organisation'
                ? organisationStatusLabel
                : null
            const organisationStatusSummary = !isApprovedServiceStatusLabel(organisationStatusLabel)
              ? getOrganisationStatusSummary(organisationStatusLabel)
              : null
            const isSelectionBlocked = isPendingUser || Boolean(effectiveRestrictionStatusLabel)
            const restrictedMessage = isSelectionBlocked
              ? isPendingUser
                ? 'Your account is still pending approval. Role switching will be available after approval is completed.'
                : getRestrictedSelectionMessage(effectiveRestrictionStatusLabel, isSelected, restrictionScope ?? 'role')
              : null
            const actionLabel = isSelectionBlocked
              ? isPendingUser
                ? 'Awaiting Account Approval'
                : getRestrictedSelectionLabel(effectiveRestrictionStatusLabel, isSelected)
              : isSelected
                ? 'Back With Current Role'
                : isRolesSection
                  ? 'Switch To This Role'
                  : 'Continue With This Role'

            const orgLogoUrl = post.service_instance_logo || post.org_logo || null

            return (
              <Card key={postKey || String(post.id)} className={`overflow-hidden ${isSelected ? 'border-primary shadow-md' : ''}`}>
                <div className="h-0.5 w-full bg-gradient-to-r from-primary/40 via-primary/15 to-transparent" />

                <CardHeader className="p-4 pb-2">
                  <div className="flex items-center gap-3">
                    {orgLogoUrl ? (
                      <div className="shrink-0 rounded-xl border border-border/70 bg-background overflow-hidden" style={{ width: 56, height: 56 }}>
                        <img src={orgLogoUrl} alt={post.org_name || 'Organisation logo'} className="h-full w-full object-cover" />
                      </div>
                    ) : (
                      <div className="flex shrink-0 items-center justify-center rounded-xl border border-border/70 bg-primary/10 text-primary" style={{ width: 56, height: 56 }}>
                        <Building2 className="h-6 w-6" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                        {post.org_name || post.service_instance_name || '—'}
                      </p>
                      <CardTitle className="truncate text-sm leading-snug">{post.role_name || post.roles_data?.roles_name || '-'}</CardTitle>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark role'}
                        title={isBookmarked ? 'Remove bookmark' : 'Bookmark role'}
                        onClick={() => toggleBookmarkedPost(post)}
                      >
                        <Star className={isBookmarked ? 'fill-current text-amber-500' : 'text-muted-foreground'} />
                      </Button>
                      {isSelected ? <Badge className="text-[10px] py-0">Current</Badge> : null}
                      {isBookmarked && !isSelected ? <Badge variant="secondary" className="text-[10px] py-0">Starred</Badge> : null}
                      {!isBookmarked && isRecent && !isSelected ? <Badge variant="outline" className="text-[10px] py-0">Recent</Badge> : null}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3 px-4 pb-4 pt-2">
                  {organisationStatusSummary ? (
                    <div className={`rounded-lg border px-3 py-2 ${getStatusBadgeClassName(organisationStatusLabel ?? '')}`}>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em]">Organisation: {organisationStatusLabel}</p>
                      <p className="mt-0.5 text-xs leading-5">{organisationStatusSummary}</p>
                    </div>
                  ) : null}

                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-3">
                      <Layers3 className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-muted-foreground">Service Type</p>
                        <p className="font-medium text-foreground">{post.service_variant_name || post.service_variant_type || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Briefcase className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-muted-foreground">Service Branch</p>
                        {serviceBranchStatusLabel && serviceBranchStatusLabel !== APPROVED_STATUS_LABEL ? (
                          <Badge className={getStatusBadgeClassName(serviceBranchStatusLabel)}>
                            {serviceBranchStatusLabel}
                          </Badge>
                        ) : null}
                        <p className="font-medium text-foreground">{post.service_instance_name || '-'}</p>
                      </div>
                    </div>
                  </div>

                  <Button className="w-full" onClick={() => void handleSelectPost(postKey)} disabled={isSelectionBlocked || isSelectingRole}>
                    {actionLabel}
                    {!isSelectionBlocked ? <ArrowRight className="ml-2 h-4 w-4" /> : null}
                  </Button>

                  {isSelectionBlocked ? (
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground">{restrictedMessage}</p>
                      {isSelected && !isPendingUser ? (
                        <Button asChild variant="outline" className="w-full">
                          <Link to={withServiceInstanceId('/profile', postReference || serviceInstanceId || null)}>
                            Open Profile
                          </Link>
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            )
          })}
          </div>
        </>
      ) : null}
    </div>
  )
}

